import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface RiskDistributionChartProps {
  data: Array<{
    risk_level: string;
    count: number;
    percentage: number;
  }>;
  loading?: boolean;
}

const RiskDistributionChart: React.FC<RiskDistributionChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No risk data available
        </div>
      </div>
    );
  }

  const RISK_COLORS = {
    'Low': '#10b981',      // Green
    'Medium': '#f59e0b',   // Yellow
    'High': '#f97316',     // Orange
    'Critical': '#ef4444'  // Red
  };

  const formatRiskLevel = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
  };

  const total = data.reduce((sum, item) => sum + item.count, 0);
  const highRiskCount = data
    .filter(item => item.risk_level === 'High' || item.risk_level === 'Critical')
    .reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ risk_level, percentage }: any) => `${formatRiskLevel(risk_level)}: ${(percentage as number).toFixed(1)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="count"
              nameKey="risk_level"
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={RISK_COLORS[entry.risk_level as keyof typeof RISK_COLORS] || '#6b7280'} 
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} workshops (${props.payload.percentage.toFixed(1)}%)`,
                formatRiskLevel(name)
              ]}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value) => formatRiskLevel(value)}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Risk Breakdown</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => {
                const order = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
                return order[a.risk_level as keyof typeof order] - order[b.risk_level as keyof typeof order];
              })
              .map((item) => (
                <div key={item.risk_level} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: RISK_COLORS[item.risk_level as keyof typeof RISK_COLORS] || '#6b7280' }}
                    ></div>
                    <span className="text-sm font-medium">{formatRiskLevel(item.risk_level)}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{item.count}</span> workshops
                    <span className="ml-1 text-gray-500">({item.percentage.toFixed(1)}%)</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Risk Summary</h4>
          <div className="space-y-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Workshops</span>
                <span className="font-bold text-gray-900">{total}</span>
              </div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">High Risk (H+C)</span>
                <span className="font-bold text-red-600">{highRiskCount}</span>
              </div>
              <div className="text-xs text-red-500 mt-1">
                {((highRiskCount / total) * 100).toFixed(1)}% of total
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Low Risk</span>
                <span className="font-bold text-green-600">
                  {data.find(item => item.risk_level === 'Low')?.count || 0}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {data.find(item => item.risk_level === 'Low')?.percentage.toFixed(1) || '0.0'}% of total
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskDistributionChart;
